import { useEffect, useRef, useState } from 'react'
import { Check, ChevronRight, LoaderCircle, LogOut, Mail, Smartphone } from 'lucide-react'
import { EmailOtpForm } from '../../components/auth/email-otp-form'
import { PhoneOtpForm } from '../../components/auth/phone-otp-form'
import { Button } from '../../components/ui/button'
import { ApiError, api, apiErrorMessage } from '../../lib/api'
import { authClient } from '../../lib/auth-client'
import { PRODUCT_NAME, PRODUCT_NAME_AR } from '../../lib/brand'
import { useI18n, useStaffRoleLabel } from '../../lib/i18n'
import { LanguageMenu } from '../marketing/language-menu'
import { rememberSelectedBusiness } from './business-handoff'
import { BusinessRegistrationForm } from './business-registration-form'
import { BusinessSessionRateLimitError, readBusinessSession } from './business-session'

interface BusinessMembership {
  staffId: string
  organizationName: string
  organizationSlug: string
  role: string
}
type AccessStep = 'checking' | 'signin' | 'workspaces' | 'registration' | 'email-empty' | 'error'
type SignInMethod = 'phone' | 'email'

export function requiresBusinessSignIn(error: unknown) {
  return error instanceof ApiError && ([401, 403].includes(error.status)
    || error.status === 409 && error.code === 'business_context_required')
}

export function registrationSetupUrl(href: string, locale: 'ar' | 'en', organizationSlug: string) {
  const url = new URL(href)
  url.searchParams.set('business', organizationSlug)
  url.searchParams.set('lang', locale)
  url.searchParams.set('setup', '1')
  return `${url.pathname}${url.search}${url.hash}`
}

export function membershipDestination(memberships: BusinessMembership[], intendedBusiness: string | null) {
  const intended = memberships.find((membership) => membership.organizationSlug === intendedBusiness)
  if (intended) return intended.organizationSlug
  return memberships.length === 1 && !intendedBusiness ? memberships[0]!.organizationSlug : null
}

export function BusinessLogin({ onSuccess, intendedBusiness = null }: {
  onSuccess: () => void
  intendedBusiness?: string | null
}) {
  const { l, locale, setLocale } = useI18n()
  const roleLabel = useStaffRoleLabel()
  const homeHref = `/?lang=${locale}`
  const [step, setStep] = useState<AccessStep>('checking')
  const [method, setMethod] = useState<SignInMethod>('phone')
  const [creating, setCreating] = useState(false)
  const [memberships, setMemberships] = useState<BusinessMembership[]>([])
  const [userId, setUserId] = useState<string | null>(null)
  const [pendingWorkspace, setPendingWorkspace] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [attempt, setAttempt] = useState(0)
  const [retryAt, setRetryAt] = useState(0)
  const [retrySeconds, setRetrySeconds] = useState(0)
  const generation = useRef(0)
  const heading = useRef<HTMLHeadingElement>(null)
  const active = useRef(true)
  const defaultEmail = import.meta.env.DEV ? locale === 'ar' ? 'manager@riyadh.example' : 'manager@riverside.example' : ''

  const openWorkspace = async (organizationSlug: string, registrationLocale?: 'ar' | 'en') => {
    setPendingWorkspace(organizationSlug)
    setError('')
    try {
      await api('/api/access/select-business', { method: 'POST', body: JSON.stringify({ organizationSlug }) })
      rememberSelectedBusiness(organizationSlug)
      if (registrationLocale) {
        window.history.replaceState(window.history.state, '', registrationSetupUrl(window.location.href, registrationLocale, organizationSlug))
        setLocale(registrationLocale)
      }
      onSuccess()
    } finally {
      if (active.current) setPendingWorkspace('')
    }
  }

  // Both a fresh OTP and a resumed session take this path. No workspace is created here.
  const loadAccess = async (run = generation.current) => {
    if (!active.current || run !== generation.current) return
    if (Date.now() < retryAt) {
      setError(apiErrorMessage(new BusinessSessionRateLimitError(retrySeconds), locale))
      setStep('error')
      return
    }
    setStep('checking')
    setError('')
    try {
      const sessionUser = await readBusinessSession()
      if (!active.current || run !== generation.current) return
      setUserId(sessionUser?.id ?? null)
      if (!sessionUser) { setStep('signin'); return }
      const resultAccess = await api<{ memberships: BusinessMembership[] }>('/api/access/memberships')
      if (!active.current || run !== generation.current) return
      setMemberships(resultAccess.memberships)
      if (resultAccess.memberships.length === 0) {
        setStep(sessionUser.phoneNumberVerified === true ? 'registration' : 'email-empty')
        return
      }
      setStep('workspaces')
      const destination = membershipDestination(resultAccess.memberships, intendedBusiness)
      if (destination) {
        try { await openWorkspace(destination) }
        catch (value) { if (active.current) setError(apiErrorMessage(value, locale)) }
      } else if (intendedBusiness) {
        setError(l('Der Arbeitsbereich im Link ist für dieses Konto nicht verfügbar. Wähle einen verfügbaren Arbeitsbereich.', 'The workspace in the link is not available to this account. Choose an available workspace.', 'مساحة العمل المحددة في الرابط غير متاحة لهذا الحساب. اختر إحدى مساحات العمل المتاحة.', 'De werkruimte in de link is niet beschikbaar voor dit account. Kies een beschikbare werkruimte.'))
      }
    } catch (value) {
      if (!active.current || run !== generation.current) return
      if (value instanceof BusinessSessionRateLimitError) {
        setRetryAt(Date.now() + value.retryAfterSeconds * 1000)
        setRetrySeconds(value.retryAfterSeconds)
      }
      setError(apiErrorMessage(value, locale))
      setStep('error')
    }
  }
  const loadAccessRef = useRef(loadAccess)
  loadAccessRef.current = loadAccess
  useEffect(() => {
    active.current = true
    const run = ++generation.current
    // StrictMode replays mount effects synchronously. Discard that first mount
    // before it can consume a second session-read request or win a response race.
    queueMicrotask(() => {
      if (active.current && run === generation.current) void loadAccessRef.current(run)
    })
    return () => { active.current = false; generation.current++ }
  }, [])
  useEffect(() => {
    if (!retryAt) return
    const timer = window.setInterval(() => {
      const remaining = Math.max(0, Math.ceil((retryAt - Date.now()) / 1000))
      setRetrySeconds(remaining)
      if (remaining === 0) window.clearInterval(timer)
    }, 1000)
    return () => window.clearInterval(timer)
  }, [retryAt])
  useEffect(() => {
    if (step === 'registration' || step === 'workspaces' || step === 'email-empty' || step === 'error') heading.current?.focus()
  }, [step])

  const switchAccount = async (nextMethod: SignInMethod = 'phone', registration = false) => {
    if (busy || pendingWorkspace) return
    setBusy(true)
    setError('')
    try {
      const result = await authClient.signOut()
      if (result.error) throw new Error('sign_out_failed')
      // Clear the workspace choice as well as the host-only authentication session.
      await api('/api/access/clear-business', { method: 'POST' })
      generation.current++
      setUserId(null)
      setMemberships([])
      setMethod(nextMethod)
      setCreating(registration)
      setAttempt((value) => value + 1)
      setStep('signin')
    } catch (value) {
      setError(apiErrorMessage(value, locale))
    } finally { setBusy(false) }
  }
  const changeMethod = (nextMethod: SignInMethod, registration = false) => {
    generation.current++
    setMethod(nextMethod)
    setCreating(registration)
    setAttempt((value) => value + 1)
    setError('')
  }
  const run = generation.current
  const registration = step === 'registration'
  const choosing = step === 'workspaces'
  const isBusy = busy || Boolean(pendingWorkspace) || step === 'checking'
  const chooseWorkspace = async (slug: string) => {
    try { await openWorkspace(slug) }
    catch (value) { setError(apiErrorMessage(value, locale)) }
  }

  return <main className="dashboard-active dashboard-login dashboard-login--phone" data-tone="active">
    <section className="dashboard-login__brand" aria-label={locale === 'ar' ? PRODUCT_NAME_AR : PRODUCT_NAME}>
      <a href={homeHref} className="dashboard-login__wordmark">{locale === 'ar' ? PRODUCT_NAME_AR : PRODUCT_NAME}</a>
      <div className="dashboard-login__promise">
        <p>{l('Von der Buchung zum Betrieb', 'From booking to operation', 'من الحجز إلى التشغيل', 'Van boeking naar bedrijfsvoering')}</p>
        <h1>{l('Mach deinen Arbeitstag buchbar.', 'Make the whole working day bookable.', 'كل حجوزات منشأتك في مكان واحد.', 'Maak je hele werkdag boekbaar.')}</h1>
        <div className="dashboard-login__route" aria-hidden="true"><span>{l('Angebot', 'Your offer', 'خدماتك', 'Je aanbod')}</span><i /><span>{l('Verfügbarkeit', 'Availability', 'التوفر', 'Beschikbaarheid')}</span><i /><span>{l('Buchung', 'Booking', 'الحجز', 'Boeking')}</span></div>
      </div>
      <p className="dashboard-login__foot">{locale === 'ar' ? 'سِعَة · إدارة الحجوزات' : 'Seaa · booking operations'}</p>
    </section>
    <section className="dashboard-login__form-shell" aria-labelledby="business-login-heading"><div className="dashboard-login__form">
      <div className="dashboard-login__language"><LanguageMenu locale={locale} onLocaleChange={setLocale} label={l('Sprache wählen', 'Choose language', 'اختيار اللغة', 'Taal kiezen')} /></div>
      <span className="dashboard-login__eyebrow">{registration || creating ? l('Dein Unternehmen auf Seaa', 'Your business on Seaa', 'منشأتك على سِعَة', 'Je bedrijf op Seaa') : l('Für Unternehmen', 'Business sign in', 'دخول المنشآت', 'Inloggen voor bedrijven')}</span>
      <h2 id="business-login-heading" ref={heading} tabIndex={-1}>{registration ? l('Erzähl uns von deinem Unternehmen', 'Tell us about your business', 'عرّفنا بمنشأتك', 'Vertel ons over je bedrijf')
        : choosing ? l('Arbeitsbereich wählen', 'Choose a workspace', 'اختر مساحة العمل', 'Kies een werkruimte')
          : creating ? l('Unternehmenskonto erstellen', 'Create your business account', 'أنشئ حساب منشأتك', 'Maak je bedrijfsaccount aan')
            : l('Öffne deinen Arbeitsbereich', 'Enter your workspace', 'ادخل إلى مساحة عملك', 'Open je werkruimte')}</h2>
      <p>{registration ? l('Nur diese Angaben für den Start. Danach richtest du deine Leistungen und Zeiten ein.', 'Just these details to get started. Next, set up your services and hours.', 'هذه البيانات تكفي للبدء، ثم تضيف خدماتك وأوقات العمل.', 'Met deze gegevens kun je beginnen. Daarna stel je diensten en tijden in.')
        : choosing ? l('Wähle das Unternehmen, das du verwalten möchtest.', 'Choose the business you want to manage.', 'اختر المنشأة التي تريد إدارتها.', 'Kies het bedrijf dat je wilt beheren.')
          : step === 'email-empty' ? l('Mit diesem Konto ist kein aktiver Arbeitsbereich verbunden. Nutze deine bestätigte Mobilnummer, um ein Unternehmen anzulegen.', 'No active workspace is linked to this account. Use your verified mobile number to create a business account.', 'لا توجد مساحة عمل نشطة مرتبطة بهذا الحساب. استخدم رقم جوالك المؤكّد لإنشاء حساب منشأة.', 'Aan dit account is geen actieve werkruimte gekoppeld. Gebruik je geverifieerde mobiele nummer om een bedrijfsaccount aan te maken.')
            : method === 'email' ? l('Für bestehende und eingeladene Mitarbeitende: Nutze die E-Mail-Adresse deiner Einladung. Eine neue E-Mail erstellt kein Unternehmenskonto.', 'Use your enabled sign-in email or the email on your staff invitation. A contact email alone cannot sign you in; use your mobile, then enable email sign-in in Settings → Business profile.', 'استخدم البريد المفعّل للدخول أو بريد دعوة الفريق. بريد التواصل وحده لا يتيح الدخول؛ ادخل برقم جوالك ثم فعّل الدخول بالبريد من الإعدادات ← بيانات المنشأة.', 'Voor bestaande en uitgenodigde medewerkers: gebruik het e-mailadres van je uitnodiging. Een nieuw e-mailadres maakt geen bedrijfsaccount aan.')
              : creating ? l('Bestätige zuerst deine Mobilnummer. Danach ergänzt du die Angaben zu deinem Unternehmen.', 'Verify your mobile number first, then add your business details.', 'تحقق من رقم جوالك أولًا، ثم أكمل بيانات منشأتك.', 'Verifieer eerst je mobiele nummer en vul daarna je bedrijfsgegevens in.')
                : l('Ein Code auf dein Mobiltelefon bringt dich zu deinem Unternehmen. Ein Passwort ist nicht nötig.', 'A code sent to your mobile gets you into your business. No password needed.', 'ادخل إلى منشأتك برمز يصلك على جوالك، دون كلمة مرور.', 'Met een code op je mobiel open je jouw bedrijf. Geen wachtwoord nodig.')}</p>

      {(creating && step === 'signin' || registration) && <ol className="business-registration__steps" aria-label={l('Einrichtungsschritte', 'Account setup steps', 'خطوات إنشاء الحساب', 'Stappen voor je account')}>
        <li aria-current={!registration ? 'step' : undefined}><span aria-hidden="true">{registration ? <Check size={14} /> : '1'}</span>{l('Mobilnummer bestätigen', 'Verify mobile', 'التحقق من الجوال', 'Mobiel verifiëren')}</li>
        <li aria-current={registration ? 'step' : undefined}><span aria-hidden="true">2</span>{l('Unternehmensangaben', 'Business details', 'بيانات المنشأة', 'Bedrijfsgegevens')}</li>
      </ol>}
      {step === 'checking' && <div className="dashboard-login__status" role="status"><LoaderCircle className="auth-email-otp__spinner" size={20} aria-hidden="true" />{l('Zugang wird geprüft…', 'Checking your access…', 'جارٍ التحقق من صلاحية الدخول…', 'Toegang controleren…')}</div>}
      {step === 'signin' && <>
        <div className="dashboard-login__methods" role="group" aria-label={l('Anmeldemethode', 'Sign-in method', 'طريقة الدخول', 'Inlogmethode')}>
          <button type="button" data-testid="business-signin-phone" aria-pressed={method === 'phone'} onClick={() => changeMethod('phone', creating)}><Smartphone size={17} aria-hidden="true" />{l('Mobilnummer', 'Mobile number', 'رقم الجوال', 'Mobiel nummer')}</button>
          <button type="button" data-testid="business-signin-email" aria-pressed={method === 'email'} onClick={() => changeMethod('email')}><Mail size={17} aria-hidden="true" />{l('Team-E-Mail', 'Email', 'البريد الإلكتروني', 'E-mail team')}</button>
        </div>
        <div className="dashboard-login__otp">{method === 'phone'
          ? <PhoneOtpForm key={`phone-${attempt}`} locale={locale} onVerified={() => loadAccess(run)} />
          : <EmailOtpForm key={`email-${attempt}`} locale={locale} defaultEmail={defaultEmail} showLocalCode={import.meta.env.DEV} onVerified={() => loadAccess(run)} submitLabel={l('Arbeitsbereich öffnen', 'Open workspace', 'فتح مساحة العمل', 'Werkruimte openen')} />}</div>
        {!creating ? <div className="dashboard-login__signup"><p>{l('Dein Unternehmen ist neu auf Seaa?', 'New business on Seaa?', 'منشأتك جديدة على سِعَة؟', 'Nieuw bedrijf op Seaa?')}</p><button type="button" data-testid="create-business-account" onClick={() => changeMethod('phone', true)}>{l('Unternehmenskonto erstellen', 'Create business account', 'إنشاء حساب منشأة', 'Bedrijfsaccount aanmaken')}<ChevronRight size={17} aria-hidden="true" /></button></div>
          : <button type="button" className="dashboard-login__text-action" onClick={() => changeMethod('phone')}>{l('Schon ein Konto? Anmelden', 'Already have an account? Sign in', 'لديك حساب؟ تسجيل الدخول', 'Al een account? Log in')}</button>}
      </>}
      {choosing && <ul className="dashboard-login__workspaces">{memberships.map((membership) => <li key={membership.staffId}><button type="button" className="dashboard-login__workspace" disabled={isBusy} onClick={() => void chooseWorkspace(membership.organizationSlug)}>
        <span><strong>{membership.organizationName}</strong><span>{roleLabel(membership.role)}</span></span>{pendingWorkspace === membership.organizationSlug ? <LoaderCircle className="auth-email-otp__spinner" size={19} aria-hidden="true" /> : <ChevronRight size={19} aria-hidden="true" />}
      </button></li>)}</ul>}
      {registration && userId && <>
        {intendedBusiness && <p className="dashboard-login__notice">{l('Der Arbeitsbereich im Link ist nicht mit diesem Konto verbunden. Hier kannst du einen neuen erstellen oder das Konto wechseln.', 'The workspace in the link is not linked to this account. You can create a new workspace here or switch accounts.', 'مساحة العمل المحددة في الرابط غير مرتبطة بهذا الحساب. يمكنك إنشاء مساحة جديدة هنا أو تبديل الحساب.', 'De werkruimte in de link hoort niet bij dit account. Maak hier een nieuwe werkruimte aan of wissel van account.')}</p>}
        <BusinessRegistrationForm key={userId} userId={userId} onBusyChange={setBusy} onCreated={openWorkspace} />
      </>}
      {step === 'email-empty' && <div className="dashboard-login__signup"><Button data-testid="create-business-account" type="button" disabled={isBusy} onClick={() => void switchAccount('phone', true)}>{l('Mit Mobilnummer fortfahren', 'Continue with mobile', 'المتابعة برقم الجوال', 'Doorgaan met mobiel')}</Button><p>{l('Wenn du dem Team eines bestehenden Unternehmens angehörst, bitte dessen Administrator um eine Einladung.', 'If you work for an existing business, ask its administrator for an invitation.', 'إذا كنت ضمن فريق منشأة قائمة، فاطلب دعوة من مسؤولها.', 'Werk je voor een bestaand bedrijf? Vraag de beheerder om een uitnodiging.')}</p></div>}
      {error && <p role="alert" className="dashboard-login__error">{error}</p>}
      {step === 'error' && <Button className="mt-5 w-full" disabled={isBusy || retrySeconds > 0} onClick={() => void loadAccess()}>{l('Erneut versuchen', 'Try again', 'إعادة المحاولة', 'Opnieuw proberen')}{retrySeconds > 0 ? ` (${retrySeconds})` : ''}</Button>}
      {(userId || step === 'error') && <button type="button" className="dashboard-login__text-action" disabled={isBusy} onClick={() => void switchAccount()}><LogOut size={16} aria-hidden="true" />{l('Anderes Konto verwenden', 'Switch account', 'تبديل الحساب', 'Ander account gebruiken')}</button>}
      <a href={homeHref} className="dashboard-login__back">{l('Zurück zu Seaa', 'Back to Seaa', 'العودة إلى سِعَة', 'Terug naar Seaa')}</a>
    </div></section>
  </main>
}
